/* eslint-disable no-console */
import React, { useContext, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js'
import { navigate } from 'gatsby'
import PropTypes from 'prop-types'
import BigNumber from 'bignumber.js'
import { Clipboard } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import RegisterLayout from '~components/RegisterLayout'
import CodeInputs from '~components/CodeInputs'
import SubmitButton from '~components/SubmitButton'

import { UserContext } from '~context/provider'
import { fetchWallet } from '~utils/fetchWallet'
import { fetchWalletBalance } from '~utils/fetchBalance'
import { codeSchema } from '~utils/validationSchema'
import userPool from '~utils/cognito'

import * as s from './verify.module.scss'

const Verify = ({ user }) => {
  const { t } = useTranslation()
  const { setUser } = useContext(UserContext)
  const [errorMessage, setErrorMessage] = useState(null)
  const [loading, setLoading] = useState(false)

  const handleSubmit = (values) => {
    setLoading(true)
    const code = values.code.join('')

    const cognitoUser = new CognitoUser({
      Username: user?.username,
      Pool: userPool,
    })

    const authenticationDetails = new AuthenticationDetails({
      Username: user?.username,
      Password: user?.password,
    })

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (session) => {
        console.log('%c session:', 'color: green', session)
        setLoading(false)
        navigate('/dashboard')
      },
      onFailure: (error) => {
        console.log('%c error Verify:', 'color: red', error)
        setErrorMessage(error.message)
        setLoading(false)
      },
      totpRequired: () => {
        cognitoUser.sendMFACode(
          code,
          {
            onSuccess: (session) => {
              console.log('%c session:', 'color: green', session)

              const accessToken = session.getAccessToken().getJwtToken()
              const idToken = session.getIdToken().getJwtToken()
              const refreshToken = session.getRefreshToken().getToken()

              cognitoUser.getUserAttributes(async (err, result) => {
                if (err) {
                  console.log(
                    '%c error on get cognito user on MFA sign in on Verify',
                    'color: red',
                    err
                  )
                  setLoading(false)
                  return
                }
                console.log('%c success:', 'color: green', result)

                const addresses = await fetchWallet(accessToken)
                const ethereumAddress = `0x${addresses.ethereum}`

                const balance = await fetchWalletBalance(ethereumAddress)

                setUser({
                  username: user?.username,
                  email: result.find((attr) => attr.Name === 'email').Value,
                  accessToken,
                  idToken,
                  refreshToken,
                  wallets: addresses,
                  ethereumAddress,
                })

                setLoading(false)

                if (new BigNumber(balance).isGreaterThan(0)) {
                  navigate('/dashboard')
                } else {
                  navigate('/buy-tokens')
                }
              })
            },
            onFailure: (error) => {
              console.log('%c error:', 'color: red', error)
              setErrorMessage(error.message)
              setLoading(false)
            },
          },
          'SOFTWARE_TOKEN_MFA'
        )
      },
    })
  }

  const formik = useFormik({
    initialValues: {
      code: Array(6).fill(''),
    },
    validationSchema: Yup.object({
      code: codeSchema,
    }),
    onSubmit: (values) => {
      handleSubmit(values)
    },
  })

  const handlePaste = async (e) => {
    e.preventDefault()

    if (!navigator.clipboard) return

    const pastedData = await navigator.clipboard.readText()
    const pastedArray = pastedData.split('')

    formik.setValues({ ...formik.values, code: pastedArray })
  }

  return (
    <RegisterLayout>
      <div className={s.confirm}>
        <p className={s.confirmSubtitle}>{t('accessCode')}</p>
        <form onSubmit={formik.handleSubmit}>
          <CodeInputs
            values={formik.values}
            handleSubmit={formik.handleSubmit}
            handleChange={formik.handleChange}
            handlePaste={handlePaste}
          />
          {errorMessage && <p className={s.error}>{errorMessage}</p>}
          <button type="button" onClick={handlePaste} className={s.copy}>
            <Clipboard size={16} /> {t('pasteCode')}
          </button>
          <SubmitButton
            title={t('sendConfirmation')}
            loading={loading}
            className={s.verifyButton}
          />
        </form>
      </div>
    </RegisterLayout>
  )
}

export default Verify

Verify.propTypes = {
  user: PropTypes.object,
}

Verify.defaultProps = {
  user: {},
}
